/**
 * Decides which mounted navigation blocker owns the browser-level unsaved-changes
 * guards (beforeunload, refresh shortcuts, popstate, link clicks, history patches).
 *
 * `DirtyTracker` mounts one blocker per layer and layers nest: a dirty Modal
 * inside a dirty Stepper arms two at once. Each one attaches its own
 * window/document listeners, and `stopPropagation` does not stop other listeners
 * on the same target — so without an owner every armed blocker reacts to the same
 * event. Two confirm dialogs stack, popstate gets pushed back twice, and the
 * navigation one blocker just confirmed is re-intercepted by the other.
 *
 * The owner is the deepest armed blocker: the innermost layer is the one the user
 * is looking at, and its message and footer actions are the specific ones. Ties
 * (sibling layers at equal depth) go to whichever registered last.
 *
 * Ownership is read at event time rather than baked into the listeners, so a
 * blocker disarming or unmounting hands over with nothing to re-install. It also
 * means a history patch left behind by an unmounted blocker (teardown is not
 * guaranteed to be LIFO — see `installHistoryPatch`) degrades to a pass-through:
 * an unregistered id never owns the guards.
 */

let entries = [];

/**
 * Marks a blocker as armed and competing for ownership.
 *
 * @param {string} id Stable per-blocker id.
 * @param {number} depth Nesting depth of the blocker's layer; deeper wins.
 * @returns {() => void} Deregisters the blocker.
 */
export const registerNavigationBlocker = (id, depth = 0) => {
  entries = [...entries.filter((entry) => entry.id !== id), { id, depth }];

  return () => {
    entries = entries.filter((entry) => entry.id !== id);
  };
};

/**
 * @param {string} id
 * @returns {boolean} Whether this blocker currently owns the browser-level guards.
 */
export const isPrimaryNavigationBlocker = (id) => {
  let primary = null;

  for (const entry of entries) {
    // `>=` so the last registration wins among equal depths.
    if (!primary || entry.depth >= primary.depth) primary = entry;
  }

  return !!primary && primary.id === id;
};

/**
 * Whether any unsaved-changes guard is armed, for actions that leave the page without a route
 * change the guards can intercept (a full reload, a practice switch).
 *
 * @returns {boolean}
 */
export const hasArmedNavigationBlocker = () => entries.length > 0 || unloadGuards.size > 0;

const unloadGuards = new Set();

/**
 * For guards that only warn on unload (no route interception of their own): they count as armed
 * for `hasArmedNavigationBlocker` without competing to be the primary blocker.
 *
 * @param {unknown} id
 * @returns {() => void} unregister
 */
export const registerUnloadGuard = (id) => {
  unloadGuards.add(id);
  return () => unloadGuards.delete(id);
};
